import { useMemo, useState, type ChangeEvent } from "react";
import { Link } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { describeError, ErrorState, Loading, NoAccess } from "../../../components/States";
import { PillView, useAction } from "../../../components/ui";
import { downloadBlob } from "../einstellungen/parts";
import { createBrand, createTalent, inviteTalent, isDuplicate, saveContact, savePayout } from "./data";
import { useBase, usePlan } from "./hooks";
import { BRAND_TEMPLATE, brandRows, creatorRows, CREATOR_TEMPLATE, parseCsv, type ImportKind, type ImportRow } from "./import-model";
import { looksLikeXlsx, readXlsx } from "./xlsx";
import { pill, type BrandPatch, type TalentPatch } from "./model";
import { DemoNote } from "./parts";
import { GrenzeWachsen } from "../../../components/Tarif";
import { t, ta } from "../../../lib/sprache";
import { creatorGrenzeText, testFreiText } from "../../../model/tarif";
import { fehlerText } from "../../../lib/fehler";

/**
 * Import aus Excel, Numbers, Google Tabellen oder einem anderen Werkzeug (Befund Einstellungen 5): Datei als CSV hochladen oder
 * Zellen einfügen, Vorschau mit Prüfung je Zeile und Dublettenhinweis, dann anlegen. Creator laufen über talent_create
 * (Tarifgrenze, Dublettenprüfung und Zuweisung in der Datenbank), Marken über die normale Anlage.
 */

const MAX_ROWS = 500;
const LABEL: Record<ImportKind, { title: string; one: string; many: string; back: string; template: string }> = {
  creator: { get title() { return t("Creator importieren", "Import creators"); }, one: "Creator", many: "Creator", back: "/agentur/creator", template: "creator-vorlage.csv" },
  marken: { get title() { return t("Marken importieren", "Import brands"); }, get one() { return t("Marke", "Brand"); }, get many() { return t("Marken", "Brands"); }, back: "/agentur/marken", template: "marken-vorlage.csv" },
};
const COLUMN_LABEL: Record<string, string> = {
  name: "Name", get first_name() { return t("Vorname", "First name"); }, get last_name() { return t("Nachname", "Last name"); }, get display_name() { return t("Anzeigename", "Display name"); }, get email() { return t("E-Mail", "Email"); }, instagram: "Instagram", tiktok: "TikTok",
  youtube: "YouTube", get whatsapp() { return t("Telefon", "Phone"); }, get street() { return t("Straße", "Street"); }, postal_code: "PLZ", get city() { return t("Ort", "City"); }, get country() { return t("Land", "Country"); }, get vat_id() { return t("USt-IdNr.", "VAT ID"); }, get tax_number() { return t("Steuernummer", "Tax number"); },
  get commission() { return t("Provision", "Commission"); }, get company_name() { return t("Firma", "Company"); }, get legal_name() { return t("Rechtlicher Name", "Legal name"); }, get invoice_email() { return t("Rechnungs-E-Mail", "Invoice email"); }, get payment_terms_days() { return t("Zahlungsziel", "Payment term"); },
  register_info: "Register", get small_business() { return t("Kleinunternehmer", "Small business"); }, get language() { return t("Sprache", "Language"); }, iban: "IBAN", get bank_account_holder() { return t("Kontoinhaber", "Account holder"); }, bic: "BIC",
  bank_name: "Bank", get invoice_route() { return t("Abrechnungsweg", "Invoicing route"); }, get contact_name() { return t("Ansprechpartner", "Contact person"); }, get contact_email() { return t("Kontakt-E-Mail", "Contact email"); }, get contact_phone() { return t("Kontakt-Telefon", "Contact phone"); },
  get contact_role() { return t("Rolle", "Role"); },
  get legal_form() { return t("Rechtsform", "Legal form"); }, get birth_date() { return t("Geburtsdatum", "Date of birth"); },
  get guardian_name() { return t("Erziehungsberechtigte", "Parent or guardian"); }, get guardian_email() { return t("E-Mail der Erziehungsberechtigten", "Parent or guardian email"); },
  get self_billing_agreed_on() { return t("Gutschriftverfahren vereinbart am", "Self-billing agreed on"); },
};
const BANK_HEADERS = /^(iban|kontoinhaber(in)?|inhaber|bic|swift|bank|bankname|kreditinstitut)$/;

/** plain: angelegt (und ggf. eingeladen) ohne Zusatzhinweis; limit: an der Tarifgrenze gescheitert (Sprache der Texte egal) */
type Result = { line: number; label: string; ok: boolean; text: string; invited?: boolean; plain?: boolean; limit?: boolean };

export function StammdatenImport({ kind }: { kind: ImportKind }) {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const plan = usePlan(ws);
  const { busy, run } = useAction();
  const [text, setText] = useState("");
  // Aus einer .xlsx gelesene Zeilen (statt Text)
  const [table, setTable] = useState<string[][] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  // Bankdaten übernimmt nur, wer sie speichern darf (Admin, Richtlinie payout_write)
  const bank = ws.isChef;
  const [withDuplicates, setWithDuplicates] = useState(false);
  // Nach dem Anlegen gleich zum Portal einladen (PRAXIS-8); nur Zeilen mit E-Mail-Adresse
  const [inviteAfter, setInviteAfter] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);
  const l = LABEL[kind];

  const parsed = useMemo(() => {
    if (!base || (!table && !text.trim())) return null;
    const rows = table ?? parseCsv(text);
    const header = rows[0] ?? [];
    const r = kind === "creator" ? creatorRows(rows, base.talents, { bank, selfBilling: ws.isChef }) : brandRows(rows, base.brands);
    const unknown = header.filter((_, i) => !Object.values(r.map).includes(i)).map((h) => h.trim()).filter(Boolean);
    const bankSkipped = kind === "creator" && !bank && header.some((h) => BANK_HEADERS.test(h.trim().toLowerCase().replace(/[^a-z]/g, "")));
    return { header, map: r.map, rows: r.rows as ImportRow<TalentPatch | BrandPatch>[], unknown, bankSkipped };
  }, [base, text, table, kind, bank]);

  if (error) return <ErrorState error={error} retry={retry} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base) return <Loading />;

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setResults(null);
    setFileError(null);
    setFileName(f.name);
    const buf = await f.arrayBuffer();
    // Excel-Datei direkt (erstes Tabellenblatt)
    if (looksLikeXlsx(f.name, new Uint8Array(buf.slice(0, 4)))) {
      try {
        setTable(await readXlsx(buf));
        setText("");
      } catch (err) {
        setTable(null);
        setFileError(fehlerText(err));
      }
      return;
    }
    // Excel speichert CSV unter Windows oft als Windows-1252; ohne gültiges UTF-8 wird so gelesen
    let content: string;
    try { content = new TextDecoder("utf-8", { fatal: true }).decode(buf); } catch { content = new TextDecoder("windows-1252").decode(buf); }
    setTable(null);
    setText(content);
  }

  const rows = parsed?.rows ?? [];
  const tooMany = rows.length > MAX_ROWS;
  const ready = rows.filter((r) => r.values && (withDuplicates || !r.duplicate));
  const faulty = rows.filter((r) => !r.values).length;
  const dupCount = rows.filter((r) => r.values && r.duplicate).length;
  const nameMissing = parsed && (kind === "creator" ? !("first_name" in parsed.map || "name" in parsed.map) : !("name" in parsed.map || "legal_name" in parsed.map));
  const active = base.talents.filter((t) => t.status !== "archived").length;
  const free = kind === "creator" && plan.data && plan.data.maxTalents >= 0 ? Math.max(0, plan.data.maxTalents - active) : null;

  // Tarifgrenze vor dem Anlegen: nur so viele Zeilen, wie Plätze frei sind; der Rest wird ausgewiesen, nicht versucht
  const creatable = free !== null ? ready.slice(0, free) : ready;
  const overLimit = ready.length - creatable.length;

  async function start() {
    const list = creatable.slice(0, MAX_ROWS);
    const out: Result[] = [];
    await run("import", async () => {
      for (const r of list) {
        try {
          if (kind === "creator") {
            const tl = await createTalent(api, orgId, r.values as TalentPatch, "active", { allowDuplicate: withDuplicates });
            let invited = "";
            if (inviteAfter && (r.values as TalentPatch).email) {
              try {
                await inviteTalent(api, tl.id);
                invited = t(", eingeladen", ", invited");
              } catch (e) {
                invited = t(`, Einladung nicht verschickt: ${describeError(e).text}`, `, invitation not sent: ${describeError(e).text}`);
              }
            }
            const p = r.extra?.payout;
            if (p && bank) {
              try {
                await savePayout(api, orgId, tl.id, { ...p, bank_country: p.iban ? p.iban.slice(0, 2) : null });
              } catch (e) {
                out.push({ line: r.line, label: r.label, ok: true, text: t(`angelegt${invited}, Bankdaten nicht gespeichert: ${describeError(e).text}`, `created${invited}, bank details not saved: ${describeError(e).text}`) });
                continue;
              }
            }
            // Gutschriftverfahren (PRAXIS-R2-8): wie im Profil nur durch den Admin
            const sb = r.extra?.selfBillingAgreedOn;
            if (sb && ws.isChef) {
              try {
                await api.update("talents", [["id", "eq", tl.id]], { self_billing_agreed_on: sb, self_billing_note: t("aus dem Import", "from the import") });
              } catch (e) {
                out.push({ line: r.line, label: r.label, ok: true, invited: !!invited && !invited.includes(":"), text: t(`angelegt${invited}, Gutschriftverfahren nicht gespeichert: ${describeError(e).text}`, `created${invited}, self-billing not saved: ${describeError(e).text}`) });
                continue;
              }
            }
            if (invited) {
              out.push({ line: r.line, label: r.label, ok: true, invited: !invited.includes(":"), plain: !invited.includes(":"), text: t(`angelegt${invited}`, `created${invited}`) });
              continue;
            }
          } else {
            const b = await createBrand(api, orgId, r.values as BrandPatch);
            const c = r.extra?.contact;
            if (c) {
              try {
                await saveContact(api, orgId, b.id, c);
              } catch (e) {
                out.push({ line: r.line, label: r.label, ok: true, text: t(`angelegt, Ansprechpartner nicht gespeichert: ${describeError(e).text}`, `created, contact person not saved: ${describeError(e).text}`) });
                continue;
              }
            }
          }
          out.push({ line: r.line, label: r.label, ok: true, plain: true, text: t("angelegt", "created") });
        } catch (e) {
          const d = describeError(e);
          out.push({ line: r.line, label: r.label, ok: false, text: isDuplicate(e) ? t("schon vorhanden (Dublette)", "already exists (duplicate)") : `${d.title} ${d.text}`.trim() });
        }
      }
      for (const r of ready.slice(creatable.length)) out.push({ line: r.line, label: r.label, ok: false, limit: true, text: t("nicht angelegt: Tarifgrenze erreicht", "not created: plan limit reached") });
    });
    setResults(out);
    setText("");
    setTable(null);
    setFileName(null);
  }

  const okCount = results?.filter((r) => r.ok).length ?? 0;
  const invitedCount = results?.filter((r) => r.ok && r.invited).length ?? 0;

  return (
    <main id="main" className="content tk-sd">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{l.title}</h1>
          <p>{t("Aus Excel, Numbers, Google Tabellen oder einem anderen Werkzeug. Erst prüfen, dann anlegen.", "From Excel, Numbers, Google Sheets or another tool. Check first, then create.")}</p>
        </div>
        <div className="row">
          <Link className="btn" to={l.back}>{t("Zurück zur Liste", "Back to the list")}</Link>
        </div>
      </div>
      <DemoNote />

      {results && (
        <div className="notice stack" role="status">
          <p><strong>{okCount === 1 ? t(`1 ${l.one} angelegt`, `1 ${l.one} created`) : `${okCount} ${l.many} angelegt`}{results.length > okCount ? t(`, ${results.length - okCount} nicht`, `, ${results.length - okCount} not`) : ""}{invitedCount ? t(`, ${invitedCount} eingeladen`, `, ${invitedCount} invited`) : ""}.</strong></p>
          {results.some((r) => !r.ok || !r.plain) && (
            <ul>
              {results.filter((r) => !r.ok || !r.plain).map((r) => <li key={r.line}>{t("Zeile ", "Row ")}{r.line} · {r.label}: {r.text}</li>)}
            </ul>
          )}
          {kind === "creator" && okCount > invitedCount && <p>{t("Creator ohne Zugang lädst du in der Liste gesammelt ein: Häkchen setzen, „Ausgewählte einladen“.", "You invite creators without access together in the list: tick them, then “Invite selected”.")}</p>}
          {results.some((r) => r.limit) && (
            <p><GrenzeWachsen art="talents" /></p>
          )}
          <div className="row">
            <Link className="btn" to={l.back}>{t("Zur Liste", "Go to list")}</Link>
            <button type="button" onClick={() => setResults(null)}>{t("Hinweis schließen", "Close notice")}</button>
          </div>
        </div>
      )}

      <section className="panel stack">
        <h2>{t("1. Tabelle vorbereiten", "1. Prepare the table")}</h2>
        <p>
          {t("Die erste Zeile enthält die Spaltennamen. Erkannt werden ", "The first row contains the column names. Recognised are ")}{kind === "creator"
            ? t(`Vorname, Nachname (oder Name), E-Mail, Instagram, TikTok, YouTube, Telefon, Straße, PLZ, Ort, Land, Steuernummer, USt-IdNr., Kleinunternehmer (ja/nein), Sprache (de/en), Firma, Rechtsform, Geburtsdatum, Erziehungsberechtigte (Name und E-Mail), Provision in Prozent${bank ? t(", IBAN, Kontoinhaber, BIC und Bank, Gutschriftverfahren vereinbart am (Datum)", ", IBAN, Kontoinhaber, BIC and Bank, Gutschriftverfahren vereinbart am (date)") : ""}`, `Vorname, Nachname (or Name), E-Mail, Instagram, TikTok, YouTube, Telefon, Straße, PLZ, Ort, Land, Steuernummer, USt-IdNr., Kleinunternehmer (ja/nein), Sprache (de/en), Firma, Rechtsform, Geburtsdatum, Erziehungsberechtigte (Name und E-Mail), Provision in Prozent${bank ? t(", IBAN, Kontoinhaber, BIC und Bank, Gutschriftverfahren vereinbart am (Datum)", ", IBAN, Kontoinhaber, BIC and Bank, Gutschriftverfahren vereinbart am (date)") : ""}`)
            : t("Name, Rechtlicher Name, Straße, PLZ, Ort, Land, USt-IdNr., Rechnungs-E-Mail, Zahlungsziel in Tagen, Register, Sprache (de/en), Abrechnungsweg (über die Agentur oder direkt an die Marke) sowie ein Ansprechpartner mit Kontakt-E-Mail, Kontakt-Telefon und Rolle (Rechnung, Rechnung in Kopie, Vertrag, Briefing, Insights)", "Name, Rechtlicher Name, Straße, PLZ, Ort, Land, USt-IdNr., Rechnungs-E-Mail, Zahlungsziel in Tagen, Register, Sprache (de/en), Abrechnungsweg (über die Agentur or direkt an die Marke) as well as a contact person with Kontakt-E-Mail, Kontakt-Telefon and Rolle (Rechnung, Rechnung in Kopie, Vertrag, Briefing, Insights)")}{t("; andere Spalten bleiben unbeachtet.", "; other columns are ignored.")}{kind === "creator"
            ? t(` Leere Provision heißt Agentur-Standard.${bank ? t(" Die IBAN wird auf ihre Prüfziffer geprüft.", " The IBAN is checked for its check digit.") : t(" Bankdaten übernimmt nur der Admin.", " Only the admin takes over bank details.")} Zum Portal lädst du mit dem Häkchen unten gleich beim Anlegen ein oder später in der Creator-Liste (mehrere auf einmal); Verträge kommen im Profil dazu.`, ` An empty commission means agency default.${bank ? t(" Die IBAN wird auf ihre Prüfziffer geprüft.", " The IBAN is checked for its check digit.") : t(" Bankdaten übernimmt nur der Admin.", " Only the admin takes over bank details.")} You invite creators to the portal with the tick below right when creating them or later in the creator list (several at once); contracts are added in the profile.`)
            : t(" Weitere Kontakte und Agenturen ergänzt du danach an der Marke.", " You add further contacts and agencies to the brand afterwards.")}
        </p>
        <div className="row">
          <button type="button" onClick={() => downloadBlob(l.template, new Blob(["\uFEFF" + (kind === "creator" ? CREATOR_TEMPLATE : BRAND_TEMPLATE)], { type: "text/csv;charset=utf-8" }))}>
            {t("Vorlage herunterladen", "Download template")}</button>
        </div>
      </section>

      <section className="panel stack">
        <h2>{t("2. Datei wählen oder Zellen einfügen", "2. Choose a file or paste cells")}</h2>
        <label>
          {t("Excel-Datei (.xlsx, erstes Tabellenblatt) oder CSV", "Excel file (.xlsx, first sheet) or CSV")}<input type="file" accept=".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => void onFile(e)} />
        </label>
        {fileName && !fileError && <small>{t("Gelesen: ", "Read: ")}{fileName}{table ? t(` · ${Math.max(0, table.length - 1)} Zeilen`, ` · ${Math.max(0, table.length - 1)} rows`) : ""}</small>}
        {fileError && <small className="tk-error" role="alert">{fileError}</small>}
        <label>
          {t("Oder Zellen hier einfügen (mit Kopfzeile)", "Or paste cells here (with header row)")}<textarea rows={5} value={text} onChange={(e) => { setText(e.target.value); setTable(null); setFileName(null); setResults(null); }} spellCheck={false} />
        </label>
      </section>

      {parsed && (
        <section className="panel stack" aria-live="polite">
          <h2>{t("3. Prüfen und anlegen", "3. Check and create")}</h2>
          <p>
            {t("Erkannte Spalten: ", "Recognised columns: ")}{Object.keys(parsed.map).map((k) => COLUMN_LABEL[k] ?? k).join(", ") || t("keine", "none")}.
            {parsed.unknown.length > 0 && t(` Nicht erkannt und ausgelassen: ${parsed.unknown.join(", ")}.`, ` Not recognised and skipped: ${parsed.unknown.join(", ")}.`)}
          </p>
          {parsed.bankSkipped && <p className="notice">{t("Bankdaten übernimmt nur der Admin; die Spalten IBAN und Kontoinhaber bleiben bei diesem Import unbeachtet.", "Only the admin takes over bank details; the IBAN and account holder columns are ignored in this import.")}</p>}
          {nameMissing && <p className="tk-error" role="alert">{t("Es fehlt eine Spalte für den Namen. Bitte die Vorlage als Muster nehmen.", "A column for the name is missing. Please use the template as a sample.")}</p>}
          {tooMany && <p className="tk-error" role="alert">{t("Die Tabelle hat ", "The table has ")}{rows.length} {t("Zeilen. Bitte in Teilen zu höchstens ", "rows. Please take over in parts of at most ")}{MAX_ROWS} {t("Zeilen importieren.", "rows.")}</p>}
          <p>
            {rows.length === 1 ? t("1 Zeile", "1 row") : t(`${rows.length} Zeilen`, `${rows.length} rows`)}: {ready.length} {t("bereit", "ready")}{faulty ? t(`, ${faulty} mit Fehlern (werden ausgelassen)`, `, ${faulty} with errors (will be skipped)`) : ""}
            {dupCount ? `, ${dupCount} ${dupCount === 1 ? t("sieht aus wie eine Dublette", "looks like a duplicate") : t("sehen aus wie Dubletten", "look like duplicates")}` : ""}.
          </p>
          {/* K-377: im Test zählen die Grenzen des vollen Umfangs; einmal, was danach gilt */}
          {kind === "creator" && plan.data?.testBis && <p className="muted">{testFreiText(plan.data.testBis, plan.data.planName, creatorGrenzeText(plan.data.planMaxTalents))}.</p>}
          {overLimit > 0 && (
            <p className="tk-error" role="alert">
              {t("Im Tarif ", "In plan ")}{plan.data?.planName} {t("sind noch ", "still has ")}{free} {t("aktive Creator frei. Angelegt werden die ersten ", "active creator slots free. The first ones created are ")}{free}; {overLimit === 1 ? t("1 Zeile bleibt", "1 row remains") : t(`${overLimit} Zeilen bleiben`, `${overLimit} rows remain`)} {t("ausgelassen.", "skipped.")}{" "}
              <GrenzeWachsen art="talents" />
            </p>
          )}
          {kind === "creator" && (
            <label className="row tk-check">
              <input type="checkbox" checked={inviteAfter} onChange={(e) => setInviteAfter(e.target.checked)} />
              <span>{t("Nach dem Anlegen zum Portal einladen (nur Zeilen mit E-Mail-Adresse; jede Person bekommt ihren eigenen Link per Mail)", "Invite to the portal after creating (only rows with an email address; each person gets their own link by email)")}</span>
            </label>
          )}
          {dupCount > 0 && (
            <label className="row tk-check">
              <input type="checkbox" checked={withDuplicates} onChange={(e) => setWithDuplicates(e.target.checked)} />
              <span>{t("Dubletten trotzdem anlegen (sonst werden sie ausgelassen)", "Create duplicates anyway (otherwise they are skipped)")}</span>
            </label>
          )}
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Vorschau", "Preview")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("Zeile", "Row")}</th>
                  <th scope="col">{l.one}</th>
                  <th scope="col">{t("Angaben", "Details")}</th>
                  <th scope="col">{t("Prüfung", "Check")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, MAX_ROWS).map((r) => (
                  <tr key={r.line}>
                    <td className="num">{r.line}</td>
                    <td><strong>{r.label}</strong></td>
                    <td><small>{summary(r)}</small></td>
                    <td>
                      <PillView pill={!r.values ? pill(t("Fehler", "Error"), "error") : r.duplicate ? pill(withDuplicates ? t("Dublette, wird angelegt", "Duplicate, will be created") : t("Dublette, ausgelassen", "Duplicate, skipped"), "warning") : pill(t("Bereit", "Ready"), "success")} />
                      {r.errors.length > 0 && <small>{r.errors.join(" · ")}</small>}
                      {r.values && r.duplicate && <small>{r.duplicate}</small>}
                      {r.values && r.notes && r.notes.length > 0 && <small>{r.notes.join(" · ")}</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row">
            <button type="button" className="primary" disabled={!!busy || !creatable.length || tooMany || !!nameMissing} onClick={() => void start()}>
              {busy === "import" ? t("Wird angelegt …", "Creating …") : creatable.length === 1 ? t(`1 ${l.one} anlegen`, `Create 1 ${l.one}`) : `${creatable.length} ${l.many} anlegen`}
            </button>
            <button type="button" disabled={!!busy} onClick={() => { setText(""); setTable(null); setFileName(null); }}>{t("Verwerfen", "Discard")}</button>
          </div>
        </section>
      )}
    </main>
  );
}

function summary(r: ImportRow<TalentPatch | BrandPatch>): string {
  if (!r.values) return "–";
  const v = r.values as Partial<TalentPatch & BrandPatch>;
  return [v.email ?? v.invoice_email, v.instagram_handle ? `@${v.instagram_handle}` : null, v.legal_name, [v.postal_code, v.city].filter(Boolean).join(" "), v.country,
    v.small_business ? t("Kleinunternehmer", "Small business") : null, r.extra?.payout ? t("mit Bankdaten", "with bank details") : null, r.extra?.contact ? t(`Kontakt ${r.extra.contact.name}`, `Contact ${r.extra.contact.name}`) : null,
    v.invoice_route === "via_agency" ? t("über die Agentur", "via the agency") : v.invoice_route === "direct_to_brand" ? t("direkt an die Marke", "directly to the brand") : null]
    .filter(Boolean).join(" · ") || t("nur Name", "name only");
}
